import { useMessageActionContext } from '@/app/providers';
import { IconMediAudio, IconMic, pause as PauseIcon, play as PlayIcon, StarIcon } from '@/assets/images';
import { Checkbox, CustomButton } from '@/components';
import AudioPlayer from '@/features/chats/sub-features/media/ui/components/Common/AudioPlayer';
import ChatProgressLoader from '@/features/chats/ui/components/Progress/ChatProgressLoader';
import MessageActionDropdown from '@/features/chats/ui/Conversation/components/MessageActionDropdown';
import { isGroupChat } from '@/features/chats/utils/jidIdFormat';
import { useMessageAction } from '@/features/chats/viewmodels/useMessageAction';
import { formatChatTime } from '@/features/timeformatter/timeStamp';
import { Username, UserProfile } from '@/features/users';
import { getLocalUserId, getMessageSenderId, millisToMinutesAndSeconds } from '@/shared/utils';
import PropTypes from 'prop-types';
import { memo, useCallback, useMemo, useRef } from 'react';
import MessageStatus from '../MessageStatus';
import ReplyMessageCard from '../ReplyMessageCard';

const BUBBLE_CLASS = {
   sender: 'sender-chat chat-bubble ct-flex ct-rowReverse ct-aiEnd ct-gap10 ct-padding-lr8-rl16',
   receiver: 'receiver-chat chat-bubble ct-flex ct-gap10 ct-padding-lr8-rl16',
};
const INNER_CLASS = {
   sender: 'ct-flex ct-flexColEnd ct-relative ct-gap4',
   receiver: 'ct-flex ct-flexCol ct-relative ct-gap4',
};

const NUM_BARS = 30;

const getBarHeightClass = h => {
   if (h < 0.25) return 'avc-bar--xs';
   if (h < 0.45) return 'avc-bar--sm';
   if (h < 0.65) return 'avc-bar--md';
   if (h < 0.82) return 'avc-bar--lg';
   return 'avc-bar--xl';
};

const WAVE_AMPLITUDES = Object.freeze(
   Array.from({ length: NUM_BARS }, (_, i) => {
      const pos = i / (NUM_BARS - 1);
      const bell = Math.sin(pos * Math.PI);
      const detail = Math.abs(Math.sin(i * 2.1) * 0.5 + Math.sin(i * 4.3 + 1) * 0.3);
      return Math.max(0.12, Math.min(1, bell * 0.65 + detail * 0.45));
   }),
);

const BAR_DESCRIPTORS = Object.freeze(
   WAVE_AMPLITUDES.map((h, i) => ({
      id: `audio-bar-${i}`,
      heightCls: getBarHeightClass(h),
   })),
);

const formatTime = s => {
   if (!Number.isFinite(s)) return '00:00';
   return `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(Math.floor(s % 60)).padStart(2, '0')}`;
};

// ── AudioTypeIcon ─────────────────────────────────────────────────────────────
const AudioTypeIcon = memo(({ audioType }) => (
   <div className="avc-type-icon ct-flexCenter">
      {audioType === 'recording' ? (
         <IconMic className="avc-type-icon__svg" />
      ) : (
         <IconMediAudio className="avc-type-icon__svg" />
      )}
   </div>
));
AudioTypeIcon.displayName = 'AudioTypeIcon';
AudioTypeIcon.propTypes = { audioType: PropTypes.string };

const AudioWaveform = memo(({ isPlaying, progress, onSeekStart, onSeek, onSeekEnd }) => {
   const containerRef = useRef(null);
   const thumbRef = useRef(null);
   const isDraggingRef = useRef(false);
   const splitIndex = Math.round((progress / 100) * NUM_BARS);

   const updateProgress = useCallback(
      (clientX, e) => {
         if (!containerRef.current) return;
         const rect = containerRef.current.getBoundingClientRect();
         // Calculate position clamped strictly within the container bounds
         const x = Math.max(0, Math.min(clientX - rect.left, rect.width));
         const percentage = (x / rect.width) * 100;

         // Bypass React render cycle for zero-latency dragging
         if (thumbRef.current) thumbRef.current.style.left = `${percentage}%`;

         // Mock the event object expected by AudioPlayer's handleSeek function without needing an input
         onSeek({
            target: { value: percentage },
            stopPropagation: () => e?.stopPropagation?.(),
            preventDefault: () => e?.preventDefault?.(),
         });
      },
      [onSeek],
   );

   const handlePointerDown = useCallback(
      e => {
         e.stopPropagation();
         isDraggingRef.current = true;
         e.currentTarget.setPointerCapture(e.pointerId);
         onSeekStart(e);
         updateProgress(e.clientX, e);
      },
      [onSeekStart, updateProgress],
   );

   const handlePointerMove = useCallback(
      e => {
         if (isDraggingRef.current) {
            updateProgress(e.clientX, e);
         }
      },
      [updateProgress],
   );

   const handlePointerUp = useCallback(
      e => {
         e.stopPropagation();
         if (isDraggingRef.current) {
            isDraggingRef.current = false;
            e.currentTarget.releasePointerCapture(e.pointerId);
            onSeekEnd(e);
         }
      },
      [onSeekEnd],
   );

   return (
      <div
         ref={containerRef}
         className={`avc-wave${isPlaying ? ' avc-wave--playing' : ''}`}
         onPointerDown={handlePointerDown}
         onPointerMove={handlePointerMove}
         onPointerUp={handlePointerUp}
         onPointerCancel={handlePointerUp}
      >
         <div className="avc-bars ct-flex ct-aiCenter">
            {BAR_DESCRIPTORS.map((bar, i) => (
               <span
                  key={bar.id}
                  className={`avc-bar ${bar.heightCls} ${i < splitIndex ? 'avc-bar--played' : 'avc-bar--unplayed'}`}
               />
            ))}
         </div>

         {progress > 0 && <span ref={thumbRef} className="avc-thumb" style={{ left: `${progress}%` }} />}
      </div>
   );
});
AudioWaveform.displayName = 'AudioWaveform';
AudioWaveform.propTypes = {
   isPlaying: PropTypes.bool.isRequired,
   progress: PropTypes.number.isRequired,
   onSeekStart: PropTypes.func.isRequired,
   onSeek: PropTypes.func.isRequired,
   onSeekEnd: PropTypes.func.isRequired,
};

const AudioBox = memo(({ message, activeChatJid, showProfile, senderId }) => {
   const { media = {}, replyTo } = message.msgBody ?? {};
   const { is_uploading, is_downloaded, duration = 0, blobUrl, file_url, audioType } = media;
   const audioSrc = blobUrl || file_url;
   const isReady = is_downloaded === 2 || (is_uploading === 2 && !!blobUrl);
   const typeIcon = <AudioTypeIcon audioType={audioType} />;

   return (
      <div className="chat-box ct-flex ct-flexCol ct-gap10 ct-relative audiobubble">
         {showProfile && <Username className="cts-text ct-username" userId={senderId} />}
         <ReplyMessageCard replyTo={replyTo} activeChatJid={activeChatJid} />

         <div className="audio-bubble-content ct-flex ct-aiCenter ct-gap10 ct-relative">
            <ChatProgressLoader
               message={message}
               autoDownload={false}
               fileSave={audioType === 'recording'}
               loaderSize="md"
            />

            <AudioPlayer
               src={audioSrc}
               render={({ state, handlers }) => {
                  const { isPlaying, progress, currentTime } = state;
                  const { togglePlayback, handleKeyDown, handleSeekStart, handleSeek, handleSeekEnd } = handlers;
                  const displayTime =
                     isPlaying || currentTime > 0 ? formatTime(currentTime) : millisToMinutesAndSeconds(duration);

                  return (
                     <>
                        {isReady && (
                           <button
                              type="button"
                              className="audio-play-btn ct-flexCenter ct-cursor"
                              onClick={togglePlayback}
                              onKeyDown={handleKeyDown}
                              aria-label={isPlaying ? 'Pause' : 'Play'}
                           >
                              {isPlaying ? <PauseIcon /> : <PlayIcon />}
                           </button>
                        )}

                        <AudioWaveform
                           isPlaying={isPlaying}
                           progress={progress ?? 0}
                           onSeek={handleSeek}
                           onSeekStart={handleSeekStart}
                           onSeekEnd={handleSeekEnd}
                        />

                        <span className="avc-duration">{displayTime}</span>
                     </>
                  );
               }}
            />

            {typeIcon}
         </div>
      </div>
   );
});
AudioBox.displayName = 'AudioBox';
AudioBox.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
   showProfile: PropTypes.bool.isRequired,
   senderId: PropTypes.string.isRequired,
};

const AudioCard = memo(({ message, activeChatJid }) => {
   const { hideAvatar = false, hideActions = false } = useMessageActionContext();
   const { isSelected, isSelectionActive, handleToggleSelection } = useMessageAction(message, activeChatJid);

   const senderId = getMessageSenderId({ type: 'chats', message });
   const isSender = senderId === getLocalUserId();
   const role = isSender ? 'sender' : 'receiver';

   const isStarred = message.favouriteStatus === 1;
   const isHighlighted = Boolean(message.isHighlighted);

   const timestamp = useMemo(
      () => (message.timestamp ? formatChatTime(new Date(Number(message.timestamp)), 'message-card') : ''),
      [message.timestamp],
   );
   const isChatGroup = useMemo(() => isGroupChat(activeChatJid), [activeChatJid]);
   const showProfile = isChatGroup && !isSender && !hideAvatar;

   const rowClassName = useMemo(() => {
      const cls = ['message-row'];
      if (isSelectionActive && !hideActions) cls.push('ct-flex ct-aiCenter selection-active');
      if (isSelected && !hideActions) cls.push('selected');
      if (isHighlighted && !hideActions) cls.push('search-highlight-active');
      return cls.join(' ');
   }, [isSelectionActive, isSelected, isHighlighted, hideActions]);

   const innerCls = `${INNER_CLASS[role]}${isStarred ? ' starredMessage ct-wFull' : ''}`;

   return (
      <CustomButton
         as="div"
         className={rowClassName}
         onClick={isSelectionActive && !hideActions ? handleToggleSelection : null}
      >
         {isSelectionActive && !hideActions && (
            <div className="selection-checkbox-wrap cts-audio">
               <Checkbox checked={isSelected} onChange={handleToggleSelection} />
            </div>
         )}

         <div className={BUBBLE_CLASS[role]}>
            {showProfile && (
               <picture className="chat-avatar cts-audio">
                  <UserProfile userId={senderId} className="chat-avatar chat-avatar-fallback ct-flexCenter" />
               </picture>
            )}

            <div className={innerCls}>
               <AudioBox
                  message={message}
                  activeChatJid={activeChatJid}
                  showProfile={showProfile}
                  senderId={senderId}
               />

               <div className={`ct-flexAlign ct-flex ct-gap8${isSender ? ' ct-rowReverse' : ''}`}>
                  {isSender && <MessageStatus msgStatus={message.msgStatus} />}
                  <span className="time cts-audio">{timestamp}</span>
                  {isStarred && (
                     <i className="icon starIcon ct-flexCenter">
                        <StarIcon />
                     </i>
                  )}
               </div>

               {!hideActions && (
                  <MessageActionDropdown
                     className="cts-audio"
                     message={message}
                     isSender={isSender}
                     activeChatJid={activeChatJid}
                  />
               )}
            </div>
         </div>
      </CustomButton>
   );
});

AudioCard.displayName = 'AudioCard';
AudioCard.propTypes = {
   message: PropTypes.object.isRequired,
   activeChatJid: PropTypes.string,
};
AudioCard.defaultProps = { activeChatJid: '' };

export default AudioCard;
